import React, { useContext, useEffect, useState } from "react";
import { TranslatorContext } from "../../../context/Translator";
import { Row, Col, Card, Form } from "react-bootstrap";
import PageLayout from "../../../layouts/PageLayout";
import { ButtonComponent } from "../../../components/elements";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import { toast, Toaster } from "react-hot-toast";

export default function LabTestTemplateCreatePage() {
    const { t } = useContext(TranslatorContext);
    const navigate = useNavigate();
    const AuthToken = localStorage.getItem("authToken");

    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [labTests, setLabTests] = useState([]);
    const [petBreeds, setPetBreeds] = useState([]);

    const [selectedLabTest, setSelectedLabTest] = useState(null);
    const [selectedPetBreed, setSelectedPetBreed] = useState(null);
    const [templateName, setTemplateName] = useState("");

    const [items, setItems] = useState([]);

    useEffect(() => {
        fetchLabTests();
        fetchPetBreeds();
    }, []);

    const fetchLabTests = async () => {
        try {
            const res = await axios.get(
                `${process.env.REACT_APP_API_URL}/lab-tests`,
                {
                    headers: { Authorization: `Bearer ${AuthToken}` },
                }
            );
            setLabTests(res.data || []);
        } catch (err) {
            toast.error("Failed to load lab tests");
        } finally {
            setLoading(false);
        }
    };

    const fetchPetBreeds = async () => {
        try {
            const res = await axios.get(
                `${process.env.REACT_APP_API_URL}/lab-template/pet-breeds`,
                {
                    headers: { Authorization: `Bearer ${AuthToken}` },
                }
            );
            setPetBreeds(res.data.data || []);
        } catch (err) {
            toast.error("Failed to load pet breeds");
        }
    };

    // =========================
    // SAFE STATE UPDATE HELPERS
    // =========================
    const addHeader = () => {
        setItems(prev => [
            ...prev,
            {
                type: "header",
                title: "",
                order: prev.length
            }
        ]);
    };

    const addField = () => {
        setItems(prev => [
            ...prev,
            {
                type: "field",
                field_name: "",
                unit: "",
                normal_min: "",
                normal_max: "",
                order: prev.length
            }
        ]);
    };

    const updateItem = (index, key, value) => {
        setItems(prev => {
            const updated = [...prev];
            if (!updated[index]) return prev;
            updated[index] = {
                ...updated[index],
                [key]: value
            };
            return updated;
        });
    };

    const removeItem = (index) => {
        setItems(prev => prev.filter((_, i) => i !== index));
    };

    // =========================
    // SUBMIT
    // =========================
    const handleSubmit = async () => {
        if (!selectedLabTest) {
            toast.error("Please select lab test");
            return;
        }

        if (!selectedPetBreed) {
            toast.error("Please select pet breed");
            return;
        }

        if (items.length === 0) {
            toast.error("Please add header or fields");
            return;
        }

        for (let i = 0; i < items.length; i++) {
            const item = items[i];

            if (item.type === "header" && !item.title?.trim()) {
                toast.error(`Header title required at row ${i + 1}`);
                return;
            }

            if (item.type === "field" && !item.field_name?.trim()) {
                toast.error(`Field name required at row ${i + 1}`);
                return;
            }
        }

        const payload = {
            lab_test_id: selectedLabTest.value,
            pet_breed_id: selectedPetBreed.value,
            name: templateName || null,
            items: items.map((item, index) => ({
                ...item,
                order: index
            }))
        };

        try {
            setSubmitting(true);

            await axios.post(
                `${process.env.REACT_APP_API_URL}/lab-test-templates`,
                payload,
                {
                    headers: {
                        Authorization: `Bearer ${AuthToken}`,
                        "Content-Type": "application/json"
                    }
                }
            );

            toast.success("Template created successfully");

            setTimeout(() => {
                navigate("/lab-test-templates");
            }, 1200);

        } catch (err) {
            toast.error(err?.response?.data?.message || "Error creating template");
        } finally {
            setSubmitting(false);
        }
    };

    const labTestOptions = (labTests || []).map(t => ({
        value: t.id,
        label: t.name
    }));

    const petBreedOptions = (petBreeds || []).map(breed => ({
        value: breed.id,
        label: breed.name
    }));

    if (loading) return <PageLayout>{t("Loading...")}</PageLayout>;

    return (
        <PageLayout>
            <Toaster position="top-right" />

            <Card className="p-4">
                {/* TOP - Added Pet Breed */}
                <Row className="mb-3">
                    <Col md={4}>
                        <Form.Group>
                            <Form.Label>Lab Test *</Form.Label>
                            <Select
                                options={labTestOptions}
                                value={selectedLabTest}
                                onChange={setSelectedLabTest}
                                placeholder="Select a lab test..."
                                styles={{
                                    control: (base) => ({
                                        ...base,
                                        border: "none",
                                        borderBottom: "1px solid #ced4da",
                                        borderRadius: 0,
                                        boxShadow: "none",
                                        "&:hover": {
                                            borderBottom: "1px solid #ced4da"
                                        }
                                    })
                                }}
                            />
                        </Form.Group>
                    </Col>

                    <Col md={4}>
                        <Form.Group>
                            <Form.Label>Pet Breed *</Form.Label>
                            <Select
                                options={petBreedOptions}
                                value={selectedPetBreed}
                                onChange={setSelectedPetBreed}
                                placeholder="Select a pet breed..."
                                styles={{
                                    control: (base) => ({
                                        ...base,
                                        border: "none",
                                        borderBottom: "1px solid #ced4da",
                                        borderRadius: 0,
                                        boxShadow: "none",
                                        "&:hover": {
                                            borderBottom: "1px solid #ced4da"
                                        }
                                    })
                                }}
                            />
                        </Form.Group>
                    </Col>

                    <Col md={4}>
                        <Form.Group>
                            <Form.Label>Template Name</Form.Label>
                            <Form.Control
                                type="text"
                                value={templateName}
                                onChange={(e) => setTemplateName(e.target.value)}
                                placeholder="Enter template name (optional)"
                                style={{
                                    border: "none",
                                    borderBottom: "1px solid #ced4da",
                                    borderRadius: 0,
                                    boxShadow: "none",
                                    paddingLeft: 0,
                                    background: "transparent"
                                }}
                            />
                        </Form.Group>
                    </Col>
                </Row>

                {/* BUTTONS */}
                <div className="d-flex gap-2 mb-3">
                    <ButtonComponent
                        className="btn btn-primary"
                        onClick={addHeader}
                        text="+ Add Header"
                    />

                    <ButtonComponent
                        className="btn btn-secondary"
                        onClick={addField}
                        text="+ Add Field"
                    />
                </div>

                {/* EMPTY */}
                {items.length === 0 && (
                    <div className="p-4 text-center border rounded">
                        No items added yet. Click "Add Header" or "Add Field" to get started.
                    </div>
                )}

                {/* LIST - Removed card borders */}
                {items.map((item, index) => (
                    <div key={index} className="p-3 mb-3" style={{ border: "none", backgroundColor: "transparent" }}>
                        {/* HEADER */}
                        {item.type === "header" && (
                            <Row>
                                <Col md={10}>
                                    <Form.Group>
                                        <Form.Label>Header Title</Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={item.title || ""}
                                            onChange={(e) =>
                                                updateItem(index, "title", e.target.value)
                                            }
                                            placeholder="Enter header title"
                                            style={{
                                                border: "none",
                                                borderBottom: "1px solid #ced4da",
                                                borderRadius: 0,
                                                boxShadow: "none",
                                                paddingLeft: 0,
                                                background: "transparent"
                                            }}
                                        />
                                    </Form.Group>
                                </Col>

                                <Col md={2}>
                                    <ButtonComponent
                                        className="btn btn-danger mt-4"
                                        onClick={() => removeItem(index)}
                                        text="Remove"
                                    />
                                </Col>
                            </Row>
                        )}

                        {/* FIELD */}
                        {item.type === "field" && (
                            <Row>
                                <Col md={4}>
                                    <Form.Group>
                                        <Form.Label>Field Name *</Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={item.field_name || ""}
                                            onChange={(e) =>
                                                updateItem(index, "field_name", e.target.value)
                                            }
                                            placeholder="Enter field name"
                                            style={{
                                                border: "none",
                                                borderBottom: "1px solid #ced4da",
                                                borderRadius: 0,
                                                boxShadow: "none",
                                                paddingLeft: 0,
                                                background: "transparent"
                                            }}
                                        />
                                    </Form.Group>
                                </Col>

                                <Col md={2}>
                                    <Form.Group>
                                        <Form.Label>Unit</Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={item.unit || ""}
                                            onChange={(e) =>
                                                updateItem(index, "unit", e.target.value)
                                            }
                                            placeholder="e.g., mg/dL"
                                            style={{
                                                border: "none",
                                                borderBottom: "1px solid #ced4da",
                                                borderRadius: 0,
                                                boxShadow: "none",
                                                paddingLeft: 0,
                                                background: "transparent"
                                            }}
                                        />
                                    </Form.Group>
                                </Col>

                                <Col md={2}>
                                    <Form.Group>
                                        <Form.Label>Min Range</Form.Label>
                                        <Form.Control
                                            type="number"
                                            value={item.normal_min || ""}
                                            onChange={(e) =>
                                                updateItem(index, "normal_min", e.target.value)
                                            }
                                            placeholder="Min value"
                                            style={{
                                                border: "none",
                                                borderBottom: "1px solid #ced4da",
                                                borderRadius: 0,
                                                boxShadow: "none",
                                                paddingLeft: 0,
                                                background: "transparent"
                                            }}
                                        />
                                    </Form.Group>
                                </Col>

                                <Col md={2}>
                                    <Form.Group>
                                        <Form.Label>Max Range</Form.Label>
                                        <Form.Control
                                            type="number"
                                            value={item.normal_max || ""}
                                            onChange={(e) =>
                                                updateItem(index, "normal_max", e.target.value)
                                            }
                                            placeholder="Max value"
                                            style={{
                                                border: "none",
                                                borderBottom: "1px solid #ced4da",
                                                borderRadius: 0,
                                                boxShadow: "none",
                                                paddingLeft: 0,
                                                background: "transparent",
                                            }}
                                        />
                                    </Form.Group>
                                </Col>

                                <Col md={2}>
                                    <ButtonComponent
                                        className="btn btn-danger mt-4"
                                        onClick={() => removeItem(index)}
                                        text="Remove"
                                    />
                                </Col>
                            </Row>
                        )}
                    </div>
                ))}

                {/* SUBMIT */}
                {items.length > 0 && (
                    <ButtonComponent
                        className="btn btn-success w-100 mt-3"
                        onClick={handleSubmit}
                        text={submitting ? "Saving..." : "Create Template"}
                    />
                )}
            </Card>
        </PageLayout>
    );
}